import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { getCountDown } from '@/utils/countDown';
import styled from 'styled-components';

type Props = {
    className?: string;
    date: number;
    forceYear?: boolean;
    showDate?: boolean;
};

const CatalogDate = ({ className = '', date = 0, forceYear = true, showDate = true }: Props) => {
    let timeoutRef = React.useRef<ReturnType<typeof setTimeout>>();
    let dateSpanRef = React.useRef<HTMLSpanElement>();
    let { countDown: openingCountDown, dateFormat: openingDateFormat } = getCountDown(date);
    const [countDown, setCountDown] = React.useState(openingCountDown);
    const [dateFormat, setDateFormat] = React.useState(openingDateFormat);

    const updateCountdown = React.useCallback(() => {
        // we check to see if the component still exists so that we dont try and set state on an unmounted component
        if (dateSpanRef) {
            const { countDown, dateFormat } = getCountDown(date, forceYear);
            setCountDown(countDown);
            setDateFormat(dateFormat);
            if (countDown) {
                timeoutRef.current = setTimeout(updateCountdown, 30000);
            }
        }
    }, [date, forceYear]);

    // this causes a rerender on a 30 sec schedule, if countdown is true
    React.useEffect(() => {
        return () => {
            if (countDown) {
                timeoutRef.current = setTimeout(updateCountdown, 30000);
            }
        };
    }, [countDown, updateCountdown]);

    // if a date is passed, set the countdown to trigger rerender on schedule
    React.useEffect(() => {
        if (date) {
            updateCountdown();
        }
    }, [date, updateCountdown]);

    let displayDate = countDown ? (
        <CountdownSpan>
            <FormattedMessage id="left" values={{ dateFormat }} />
        </CountdownSpan>
    ) : (
        <span>{dateFormat}</span>
    );

    if (!showDate) {
        return null;
    }
    return (
        <DateSpan className={className} ref={(ref) => (dateSpanRef.current = ref)}>
            {displayDate}
        </DateSpan>
    );
};

export default React.memo<Props>(CatalogDate);

const DateSpan = styled.span`
    font-size: 14px;
    color: ${({ theme }) => theme.colors.grey100};

    @include respond-to2(xxs, xs) {
        font-size: 12px;
    }
`;

DateSpan.displayName = 'DateSpan';

const CountdownSpan = styled.span`
    &&& {
        color: ${({ theme }) => theme.colors.red100};
        text-transform: capitalize;
    }
`;

CountdownSpan.displayName = 'CountdownSpan';
